// Requests plugin.
import { RESPONSE } from './interface/actionTypes'
import * as actions from './interface/actions'
import { getCommonOptions } from './utils'

// Libraries.
import { mergeValues } from '../common/utils'
import { get } from 'lodash/fp'
import { put, take, call } from 'redux-saga/effects'

/**
 * Creates an effect description that instructs the middleware to perform a request with the provided options.
 * This effect is blocking and will yield a RESPONSE action on completion.
 *
 * @param {Object} options See https://developer.mozilla.org/en-US/docs/Web/API/Request/Request
 * @param {string} options.url The url to perform the request on.
 * @param {string} options.method The HTTP method to use for the request.
 * @param {Object} options.headers Object literal of headers you want to add to the request.
 * @param {Object} [options.queryParams] The parameters to be added to the query string
 * @param {string} [options.responseType] The data type assumed to be received in the response body
 * @param {Blob|BufferSource|FormData|UrlSearchParams|string} [options.body] Any body that you want to add to your request.
 * @return A blocking redux-saga effect that will instruct the middleware to wait for the request to be fulfilled or until it fails.
 */
export default function request (options, commonOptions) {
  return call(requestSaga, options, commonOptions)
}

/*
 * The saga backing the request effect.
 */
export function * requestSaga (options, manualOptions) {
  /*
   * Some requests can have special-cases where they don't want to use the
   *    "common" options. Allow them to pass in "manual" options that should be
   *    used instead.
   * For example, the CPaaS "upload file" request cannot use the "common"
   *    Content-Type header.
   */
  if (manualOptions) {
    options = mergeValues(options, manualOptions)
  } else {
    // Get the common request options that should be used for all requests.
    const commonOptions = yield call(getCommonOptions, options.url)

    options = mergeValues(options, commonOptions)
  }

  // Dispatch the request action for the sagas to process.
  const requestAction = yield put(actions.request(options))
  const responseAction = yield take(
    action => action.type === RESPONSE && get('meta.requestId', action) === requestAction.meta.requestId
  )

  return responseAction
}
